/**
 * Simple toast notification system.
 * Usage: wrap app in <ToastProvider>, call useToast() anywhere to get showToast().
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { CheckCircle, AlertTriangle, Info } from "lucide-react";

type ToastKind = "success" | "error" | "info";

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // No-op if provider not mounted — keep app working even without provider
    return { showToast: () => {} };
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, kind: ToastKind = "success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, kind, message }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 1800);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const config = {
    success: { bg: "bg-brand", text: "text-white", Icon: CheckCircle },
    error: { bg: "bg-danger", text: "text-white", Icon: AlertTriangle },
    info: { bg: "bg-info", text: "text-white", Icon: Info },
  }[toast.kind];

  const Icon = config.Icon;

  return (
    <div
      className={`${config.bg} ${config.text} pointer-events-auto rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 text-[14px] font-medium animate-[fadeIn_0.2s_ease-out]`}
      style={{ minWidth: 200, maxWidth: 400 }}
    >
      <Icon size={16} />
      <span className="flex-1">{toast.message}</span>
    </div>
  );
}
